import { getLifecycleTypeName } from "./lifecycleUtils";

type LifecycleKey = "LEAD" | "PIPELINE" | "DEAL" | "PROPOSAL" | "CONTRACT";

interface ApiMessage {
  field: string | null;
  message: string;
}

const GENERAL_KEYS = ["detail", "message", "error", "errors", "non_field_errors"];

const FIELD_ENTITIES: Record<string, LifecycleKey> = {
  related_lead_id: "LEAD",
  realted_lead_id: "LEAD",
  lead_id: "LEAD",
  related_pipeline_id: "PIPELINE",
  pipeline_id: "PIPELINE",
  related_deal_id: "DEAL",
  deal_id: "DEAL",
  related_proposal_id: "PROPOSAL",
  proposal_id: "PROPOSAL",
};

const ENTITY_WORDS: Record<string, LifecycleKey> = {
  lead: "LEAD",
  pipeline: "PIPELINE",
  deal: "DEAL",
  proposal: "PROPOSAL",
  contract: "CONTRACT",
};

const DUPLICATE_PATTERN = /an?\s+(?:active\s+)?(\w+)\s+already exists for this\s+(\w+)/i;

const collectMessages = (data: unknown, field: string | null = null): ApiMessage[] => {
  if (data === null || data === undefined) return [];
  if (typeof data === "string") return data.trim() ? [{ field, message: data.trim() }] : [];
  if (Array.isArray(data)) return data.flatMap((item) => collectMessages(item, field));
  if (typeof data === "object") {
    return Object.entries(data as Record<string, unknown>).flatMap(([key, value]) =>
      collectMessages(value, GENERAL_KEYS.includes(key) ? field : key)
    );
  }
  return [];
};

const stripIds = (text: string) =>
  text
    .replace(/\s*\(\s*ID\s*[:#]?\s*\d+\s*\)/gi, "")
    .replace(/\bID\s*[:#]\s*\d+\b/gi, "")
    .replace(/\s{2,}/g, " ")
    .replace(/\s+([.,;:])/g, "$1")
    .trim();

const fieldLabel = (field: string) => {
  const entity = FIELD_ENTITIES[field];
  if (entity && field.startsWith("re")) return `Related ${getLifecycleTypeName(entity)}`;
  if (entity) return getLifecycleTypeName(entity);
  const words = field.replace(/_id$/, "").replace(/_/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : field;
};

const duplicateMessage = ({ field, message }: ApiMessage) => {
  const match = message.match(DUPLICATE_PATTERN);
  if (!match) return null;
  const existing = ENTITY_WORDS[match[1].toLowerCase()];
  const parent = ENTITY_WORDS[match[2].toLowerCase()] ?? (field ? FIELD_ENTITIES[field] : undefined);
  if (!existing || !parent) return null;
  const existingName = getLifecycleTypeName(existing);
  const parentName = getLifecycleTypeName(parent);
  return `The selected ${parentName} already has an active ${existingName}. Choose a different ${parentName}, or archive or delete its existing ${existingName} first.`;
};

export const getApiErrorMessage = (error: any, fallback: string): string => {
  const status = Number(error?.response?.status);
  if (Number.isFinite(status) && status >= 500) return fallback;
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim().startsWith("<")) return fallback;
  const messages = collectMessages(data);
  for (const item of messages) {
    const friendly = duplicateMessage(item);
    if (friendly) return friendly;
  }
  const first = messages.find((item) => stripIds(item.message));
  if (!first) return fallback;
  const text = stripIds(first.message);
  return first.field ? `${fieldLabel(first.field)}: ${text}` : text;
};
